import { useEffect, useMemo, useRef, useState } from "react";
import { animate, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { RotateCcw, SkipForward } from "lucide-react";
import { buildTree } from "@/lib/tree";
import { api, eraFor, heroFilter, media, playNote } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { RollingYear } from "@/components/RollingYear";
import { MagneticButton } from "@/components/MagneticButton";
import { EASE } from "@/components/Motion";
import { useNavigate } from "react-router-dom";

const clamp = (v) => Math.max(0, Math.min(1, v));
const TITLE = "MARAIS FAMILY";

const LeafCard = ({ leaf, pos }) => {
  const sx = useSpring(pos.x, { stiffness: 260, damping: 24 });
  const sy = useSpring(pos.y, { stiffness: 260, damping: 24 });
  useEffect(() => { sx.set(pos.x); sy.set(pos.y); }, [pos, sx, sy]);
  const m = leaf?.member;
  return (
    <motion.div className="pointer-events-none fixed z-[75] w-64 glass rounded-2xl p-5" style={{ left: sx, top: sy, rotateX: 6, rotateY: -8, transformPerspective: 800 }}
      initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }} data-testid="leaf-popup">
      <p className="font-mono-x text-[11px] tracking-[0.25em] text-[#C9A24B]">GENERATION {m?.generation || "-"}</p>
      {m && !m.anon && m.name ? (
        <>
          {m.photo_url && <img src={media(m.photo_url)} alt={m.name} className="mt-3 w-full h-28 object-cover rounded-lg border border-[#C9A24B]/40" />}
          <p className="mt-3 font-display text-xl text-[#F6EFE0]">{m.name}</p>
          {(m.born || m.died) && <p className="text-sm text-[#E4C87F]">{m.born}{m.died ? ` – ${m.died}` : ""}</p>}
          {m.bio && <p className="mt-2 text-sm text-[#F6EFE0]/80 line-clamp-3">{m.bio}</p>}
        </>
      ) : (
        <p className="mt-3 font-accent italic text-lg text-[#F6EFE0]">Family member, details are private</p>
      )}
    </motion.div>
  );
};

const Leaf = ({ l, visible, glow, near, onEnter, onLeave, onTap, testid }) => (
  <motion.g initial={false} animate={{ scale: visible ? l.s * (near ? 1.35 : 1) : 0, x: near ? near.dx : 0, y: near ? near.dy : 0 }} transition={{ type: "spring", stiffness: 180, damping: 14 }}
    style={{ transformOrigin: `${l.x}px ${l.y}px`, transformBox: "view-box" }}>
    <path d={`M${l.x} ${l.y} c 6 -10 18 -10 22 0 c -4 10 -16 10 -22 0 z`} transform={`rotate(${l.rot} ${l.x} ${l.y})`}
      fill={glow ? "#E4C87F" : "#C9A24B"} opacity={glow ? 1 : 0.75} style={glow ? { filter: "drop-shadow(0 0 6px rgba(228,200,127,.9))" } : undefined} />
    {l.member && <circle cx={l.x} cy={l.y} r="16" fill="transparent" className="cursor-pointer" onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={onTap} data-testid={testid} />}
  </motion.g>
);

export const TreeHero = () => {
  const { site, sound } = useSite() || {};
  const nav = useNavigate();
  const [year, setYear] = useState(1890);
  const [members, setMembers] = useState([]);
  const [hover, setHover] = useState(null);
  const [mouse, setMouse] = useState({ x: -999, y: -999 });
  const [svgMouse, setSvgMouse] = useState(null);
  const ctrl = useRef(null);
  const svgRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [4, -4]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-1, 1], [-6, 6]), { stiffness: 60, damping: 20 });
  const tree = useMemo(() => buildTree(1890), []);

  const play = () => {
    ctrl.current?.stop();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setYear(2026); return; }
    setYear(1890);
    ctrl.current = animate(1890, 2026, { duration: 20, ease: [0.45, 0.05, 0.4, 1], onUpdate: setYear });
  };
  useEffect(() => { play(); return () => ctrl.current?.stop(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/public/tree").then((r) => setMembers(Array.isArray(r.data?.members) ? r.data.members : [])).catch(() => setMembers([]));
  }, []);

  const leaves = useMemo(() => {
    const step = Math.max(1, Math.floor(tree.leaves.length / Math.max(1, members.length)));
    return tree.leaves.map((l, i) => ({ ...l, member: i % step === 0 && i / step < members.length ? members[i / step] : null }));
  }, [tree, members]);

  const p = clamp((year - 1890) / 136);
  const era = eraFor(year);
  const old = year < 1930;

  const onMove = (e) => {
    setMouse({ x: e.clientX, y: e.clientY });
    mx.set((e.clientX / window.innerWidth) * 2 - 1);
    my.set((e.clientY / window.innerHeight) * 2 - 1);
    const svg = svgRef.current;
    if (svg) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      if (ctm) { const s = pt.matrixTransform(ctm.inverse()); setSvgMouse({ x: s.x, y: s.y }); }
    }
  };
  const nearOf = (l) => {
    if (!svgMouse) return null;
    const dx = svgMouse.x - l.x;
    const dy = svgMouse.y - l.y;
    const d = Math.hypot(dx, dy);
    return d < 110 ? { dx: dx * 0.12, dy: dy * 0.12 } : null;
  };
  const cursorStyle = year < 1930 ? "bg-[#F6EFE0] w-2.5 h-2.5 shadow-[0_0_0_1px_#000]" : year < 1990 ? "bg-[#C9A24B]/70 w-4 h-4 shadow-[0_0_24px_8px_rgba(201,162,75,.45)]" : "bg-[#E4C87F] w-3 h-3 shadow-[0_0_18px_6px_rgba(228,200,127,.8),0_0_40px_16px_rgba(120,200,140,.25)]";

  return (
    <section className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#0B1D14] md:cursor-none" onMouseMove={onMove} onMouseLeave={() => { setMouse({ x: -999, y: -999 }); setSvgMouse(null); }} data-testid="hero-section">
      <div className="absolute inset-0 era-filter" style={{ filter: heroFilter(year) }}>
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 60% 30%, ${year > 1990 ? "rgba(201,162,75,.28)" : "rgba(246,239,224,.08)"} 0%, transparent 60%), linear-gradient(180deg, #0F2A1D 0%, #0B1D14 100%)` }} />
        {site?.hero_video_url ? (
          <video src={media(site.hero_video_url)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-80" data-testid="hero-video" />
        ) : (
          <motion.div className="absolute inset-0 flex items-end justify-center md:justify-end md:pr-[4vw]" style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}>
            <svg ref={svgRef} viewBox="0 0 1000 860" className="h-[92%] w-auto max-w-none opacity-35 md:opacity-100" data-testid="hero-tree-svg">
              <defs>
                <linearGradient id="bark" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stopColor="#8A6D2C" /><stop offset="1" stopColor="#E4C87F" /></linearGradient>
              </defs>
              <ellipse cx="500" cy="768" rx={40 + p * 260} ry={6 + p * 14} fill="#C9A24B" opacity={0.08 + p * 0.1} />
              {tree.roots.map((d, i) => <path key={i} d={d} stroke="#8A6D2C" strokeWidth="3" fill="none" strokeLinecap="round" pathLength="1" strokeDasharray="1" strokeDashoffset={1 - clamp(p / 0.08)} opacity=".7" />)}
              {tree.branches.map((b, i) => (
                <path key={i} d={b.d} stroke="url(#bark)" strokeWidth={Math.max(0.8, b.width)} fill="none" strokeLinecap="round" pathLength="1" strokeDasharray="1" strokeDashoffset={1 - clamp((p - b.start) / 0.13)} />
              ))}
              {leaves.map((l, i) => (
                <Leaf key={i} l={l} visible={p >= l.t || (l.member && p > 0.7)} glow={!!l.member || year > 1995} near={nearOf(l)}
                  onEnter={() => { setHover(l); playNote(sound, 660); }} onLeave={() => setHover(null)} onTap={() => setHover((h) => (h === l ? null : l))} testid={l.member ? `tree-leaf-${i}` : undefined} />
              ))}
              <motion.circle cx="500" cy="766" r="7" fill="#E4C87F" initial={{ scale: 0 }} animate={{ scale: p < 0.05 ? 1 : 0.4 }} />
            </svg>
          </motion.div>
        )}
        {[...Array(18)].map((_, i) => (
          <motion.span key={i} className="absolute rounded-full bg-[#E4C87F]" style={{ left: `${(i * 53) % 100}%`, top: `${(i * 37) % 100}%`, width: i % 3 ? 3 : 7, height: i % 3 ? 3 : 4, opacity: 0.35 }}
            animate={{ y: [0, 60, 120], x: [0, 20, -10], opacity: [0, 0.5, 0], rotate: [0, 90, 180] }} transition={{ duration: 9 + (i % 5) * 2, repeat: Infinity, delay: i * 0.6, ease: "linear" }} />
        ))}
        {old && <><div className="scratches flicker" /><div className="vignette" /></>}
        {!old && year < 1960 && <div className="vignette opacity-70" />}
      </div>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-20 pointer-events-none">
        <p className="font-mono-x text-xs tracking-[0.35em] text-[#C9A24B]" data-testid="hero-era-label">{era.label.toUpperCase()} · {era.range[0]}–{era.range[1]}</p>
        <h1 className="mt-5 font-display text-[2.9rem] sm:text-6xl lg:text-8xl tracking-tight text-[#F6EFE0] leading-[0.95]" data-testid="hero-title">
          <span className="sr-only">{TITLE}</span>
          {TITLE.split(" ").map((w, wi) => (
            <span key={wi} aria-hidden="true" className="inline-block whitespace-nowrap mr-[0.25em]">
              {w.split("").map((c, ci) => {
                const i = wi * 7 + ci;
                return (
                  <motion.span key={ci} className="inline-block" initial={{ opacity: 0, y: 40, filter: "blur(10px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 1, ease: EASE, delay: 0.5 + i * 0.07 }} style={{ textShadow: "0 0 28px rgba(228,200,127,.35)" }}>{c}</motion.span>
                );
              })}
            </span>
          ))}
        </h1>
        <div className="mt-6 overflow-hidden">
          <motion.p className="font-accent italic text-2xl md:text-3xl text-[#E4C87F] max-w-xl" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, ease: EASE, delay: 1.5 }} data-testid="hero-slogan">
            {site?.slogan || "Every leaf or every branches makes a great tree"}
          </motion.p>
        </div>
        <motion.div className="mt-10 flex flex-wrap gap-4 pointer-events-auto" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.9, duration: 0.8 }}>
          <MagneticButton testid="hero-explore-btn" onClick={() => { const el = document.getElementById("journey"); window.__lenis ? window.__lenis.scrollTo(el) : el?.scrollIntoView({ behavior: "smooth" }); }}>Explore Our Story</MagneticButton>
          <MagneticButton className="btn-ghost" testid="hero-members-btn" onClick={() => nav("/gate")}>Family Members Only</MagneticButton>
        </motion.div>
      </div>

      <div className="absolute z-20 right-6 md:right-10 top-24 md:top-28 text-right">
        <RollingYear year={year} className="text-4xl md:text-7xl lg:text-8xl text-[#F6EFE0]/95" testid="hero-year-counter" />
        <div className="mt-4 flex justify-end gap-2">
          <button className="lb-btn !w-9 !h-9" onClick={play} aria-label="Replay" data-testid="hero-replay-btn"><RotateCcw size={14} /></button>
          <button className="lb-btn !w-9 !h-9" onClick={() => { ctrl.current?.stop(); setYear(2026); }} aria-label="Skip" data-testid="hero-skip-btn"><SkipForward size={14} /></button>
        </div>
      </div>

      {hover && <LeafCard leaf={hover} pos={{ x: Math.min(mouse.x + 22, window.innerWidth - 280), y: Math.min(mouse.y + 18, window.innerHeight - 260) }} />}
      <motion.div className={`hidden md:block pointer-events-none fixed z-[76] rounded-full -translate-x-1/2 -translate-y-1/2 ${cursorStyle}`} style={{ left: mouse.x, top: mouse.y }} data-testid="hero-cursor" />
    </section>
  );
};
